"use client";

import { ArrowRight, Building2, ShieldCheck, Lock, Briefcase, HeartPulse, TrendingDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Pillars() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const spheres = [{
    icon: ShieldCheck,
    title: "Tax Compliance",
    description: "Precision-engineered tax filings that ensure total regulatory adherence while identifying every available credit for your Alberta-based enterprise."
  }, {
    icon: Building2,
    title: "Operational Workflow",
    description: "We architect the internal financial systems that streamline your daily operations, from inventory management to automated expense tracking."
  }, {
    icon: Lock,
    title: "Cash Flow Strategy",
    description: "Real-time monitoring and forecasting to ensure your liquidity remains robust, allowing you to reinvest in growth with absolute confidence."
  }, {
    icon: Briefcase,
    title: "Strategic Advisory",
    description: "High-level guidance for business owners, focusing on long-term scalability, exit planning, and structural optimization for your specific industry."
  }, {
    icon: HeartPulse,
    title: "KPI Performance",
    description: "Transforming raw data into actionable intelligence, we track the metrics that actually drive profitability and operational health."
  }, {
    icon: TrendingDown,
    title: "Asset Management",
    description: "Specialized oversight for capital-intensive businesses, optimizing the lifecycle and tax efficiency of your assets."
  }];
  return <section data-section-id="11465" id="strategic-pillars" className="bg-background text-foreground py-24 md:py-36">
      <div className="container mx-auto px-5 md:px-8 max-w-[1280px]">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-6 font-default">
              Our Core Pillars
            </div>
            <div className="w-16 h-px bg-border mb-8" />
            <h2 className="font-serif font-normal text-foreground text-[clamp(2rem,3.6vw,3.25rem)] leading-[1.08] tracking-[-0.018em]">
              The architectural{" "}
              <span className="relative inline-block italic">
                framework
                <span className={`absolute left-0 -bottom-1 h-px bg-primary transition-all duration-500 ease-out ${header.isVisible ? "w-full" : "w-0"}`} style={{
                transitionDelay: header.isVisible ? "200ms" : "0ms"
              }} />
              </span>{" "}
              for your scale
            </h2>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-end">
            <p className="text-[clamp(1.125rem,1.2vw,1.25rem)] leading-[1.6] tracking-[-0.003em] text-muted-foreground font-default">
              ScaleBase integrates tax compliance with high-level business advisory. 
              We build the financial infrastructure that allows Alberta’s leaders 
              to transition from manual bookkeeping to automated, scalable growth.
            </p>
            <div className="mt-6">
              <Link to="#scale-ready-assessment" className="group inline-flex items-center text-sm font-medium text-foreground font-default">
                
                
              </Link>
            </div>
          </div>
        </div>

        {}
        <div ref={grid.ref} className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {spheres.map((sphere, i) => {
          const Icon = sphere.icon;
          return <div key={i} data-index={i} className={`group bg-card border border-border rounded-lg p-8 flex flex-col transition-all duration-300 ease-out hover:border-primary hover:-translate-y-[4px] shadow-md ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: grid.isVisible ? `${i * 80}ms` : "0ms",
            transitionDuration: grid.isVisible ? "700ms" : "300ms"
          }}>
                <Icon className="size-6 text-primary mb-8" strokeWidth={1.5} />
                <h3 className="font-serif text-foreground text-[clamp(1.375rem,1.8vw,1.75rem)] leading-[1.18] tracking-[-0.012em] font-normal">
                  {sphere.title}
                </h3>
                <div className="w-8 h-px bg-border my-6" />
                <p className="text-base leading-[1.65] text-muted-foreground font-default flex-grow">
                  {sphere.description}
                </p>
                <div className="mt-8">
                  <Link to="/HomePage" className="inline-flex items-center text-sm font-medium text-foreground font-default">
                    <span className="relative">
                      Learn more
                      <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-foreground transition-all duration-[250ms] ease-out group-hover:w-full" />
                    </span>
                    <ArrowRight className="ml-2 size-4 transition-transform duration-[250ms] ease-out group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}